import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  handleSuccessPurchase,
  setPending,
} from "@web-solutions/core/store/billing/actions";

import { PaymentSystem } from "..";

import type { ApplePayButtonTheme, SdkInstance } from "./truegate-sdk";

/**
 * Тёмная тема воронки: чёрная кнопка Apple сливается с чёрным фоном, нужна белая.
 * Кнопку рисует SDK Truegate, и её вид задаётся опцией THEME (дефолт — BLACK),
 * которая уезжает в атрибут `buttonstyle` веб-компонента Apple.
 *
 * Тему читаем из `data-theme` (его ставит applyTheme из core), а не из remote
 * config: пакет не должен зависеть от хуков приложения. Смотрим на ближайший
 * помеченный контейнер, а не сразу на `:root`: попап оплаты остаётся белым при
 * любой теме и помечает себя data-theme="light" — на нём кнопка нужна чёрная.
 */
const getApplePayButtonTheme = (container: HTMLElement | null): ApplePayButtonTheme => {
  const scope = container?.closest("[data-theme]") ?? document.documentElement;

  return scope.getAttribute("data-theme") === "dark" ? "WHITE" : "BLACK";
};

export const ApplePayTruegateContainer: React.FC<any> = ({
  options,
  email,
  product,
  className,
  onSubmit,
  onSuccess,
  onReady,
  onClick,
  onError,
}) => {

  const productId = product?.truegateId || product?.productCode || "no product";
  const intent =
    options?.orderDetails?.products?.[productId] ||
    options?.orderDetails?.one_time_products?.[productId] ||
    options?.orderDetails;

  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const init = async () => {
      const ID = "APPLE_PAY_TRUEGATE";

      //@ts-ignore
      const sdkInstance: SdkInstance = new window.truegateSdk({
        id: ID,
        transactionId: intent.transactionId,
        env: process.env.REACT_APP_TRUEGATE_ENV as any,
      });

      sdkInstance.on("APPLE_PAY_READY" as any, (payload) => {
        onReady({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on("APPLE_PAY_BUTTON_CLICK" as any, (payload) => {
        onClick({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on("PAYMENT_STATUS" as any, (payload: any) => {
        if (
          payload.details.status === "FAILED" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(setPending(false));
          onError({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            currency: product.currency,
            email,
          });
          console.log("ERROR", payload);
          return;
        }
        if (
          payload.details.status === "PENDING" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(setPending(true));
          onSubmit({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            currency: product.currency,
            email,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            price_id: productId,
          });
        }
        if (
          payload.details.status === "SUCCESS" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(
            handleSuccessPurchase({
              paymentSystem: PaymentSystem.TRUEGATE,
              email,
              method: "applepay",
              amount: product.amount,
              currency: product.currency,
              subscription_id: productId,
              transaction_id: intent.transactionId,
              price_id: productId,
            } as any)
          );
          onSuccess({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            email,
            currency: product.currency,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            price_id: productId,
          });
          dispatch(setPending(false));
        }
      });

      await sdkInstance.init();
      await sdkInstance.initApplePay({
        id: `truegate-apple-pay-button-${intent.transactionId}`,
        buttonOptions: {
          HEIGHT: "52px",
          THEME: getApplePayButtonTheme(containerRef.current),
        },
      });
    };

    if (intent) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  if (!intent) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      key={`truegate-apple-pay-button-${intent.transactionId}`}
      id={`truegate-apple-pay-button-${intent.transactionId}`}
      // className нужен странице: сама кнопка живёт в shadow DOM, и её вид
      // (например радиус через --apple-pay-button-border-radius) задаётся
      // переменными, объявленными на этом контейнере
      className={className}
      style={{ width: "100%", height: "52px", border: "none" }}
    />
  );
};
